import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { User, Call, Location, People, CardPos, Bank, Teacher, Briefcase, Heart, Global, DocumentText1, Medal } from "iconsax-react";
import type { Icon } from "iconsax-react";
import { StepsSwitcher, Button, Prompt, Toast } from "@/shared/ui";
import { Loader } from "@/shared/components";
import { extractErrorMessage } from "@/shared/utils";
import {
  GeneralSection,
  ContactsSection,
  AddressesSection,
  EmergencyContactsSection,
  IdDocumentsSection,
  BankingSection,
  EducationSection,
  ExperienceSection,
  SocialCategoriesSection,
  LanguagesSection,
  ScientificWorksSection,
  GovernmentAwardsSection,
  ConfirmChangesModal,
} from "@/features/self/profile/components/personal-sheet";
import {
  EditGeneralSection,
  EditContactsSection,
  EditAddressesSection,
  EditEmergencyContactsSection,
  EditIdDocumentsSection,
  EditBankingSection,
  EditEducationSection,
  EditExperienceSection,
  EditSocialCategoriesSection,
  EditLanguagesSection,
  EditScientificWorksSection,
  EditGovernmentAwardsSection,
} from "@/features/self/profile/components/personal-sheet/edit";
import type { SectionFormHandle } from "@/features/self/profile/components/personal-sheet/edit";
import type { PersonalSheet } from "@/features/self/profile/types/personalSheet";
import { isFioChangeMissingIdFile, buildMergedSheet, enrichDraftFilesFromStaged } from "@/features/self/profile";
import type { DraftSectionKey } from "@/features/self/personal-sheet";
import {
  useCreateDataChangeForWorkerMutation,
  useGetWorkerDataChangeDraftQuery,
  useUpsertWorkerDataChangeDraftMutation,
  useDeleteWorkerDataChangeDraftMutation,
  useUploadWorkerDataChangeDraftFileMutation,
} from "@/features/self/requests/api";

type SectionId =
  | "general"
  | "contacts"
  | "addresses"
  | "emergency"
  | "id_documents"
  | "banking"
  | "education"
  | "experience"
  | "languages"
  | "scientific_works"
  | "government_awards"
  | "social_categories";

const SECTIONS: { id: SectionId; icon: Icon; labelKey: string }[] = [
  { id: "general", icon: User, labelKey: "personalSheet.sections.general" },
  { id: "contacts", icon: Call, labelKey: "personalSheet.sections.contacts" },
  { id: "addresses", icon: Location, labelKey: "personalSheet.sections.addresses" },
  { id: "emergency", icon: People, labelKey: "personalSheet.sections.emergency" },
  { id: "id_documents", icon: CardPos, labelKey: "personalSheet.sections.idDocuments" },
  { id: "banking", icon: Bank, labelKey: "personalSheet.sections.banking" },
  { id: "education", icon: Teacher, labelKey: "personalSheet.sections.education" },
  { id: "experience", icon: Briefcase, labelKey: "personalSheet.sections.experience" },
  { id: "languages", icon: Global, labelKey: "personalSheet.sections.languages" },
  { id: "scientific_works", icon: DocumentText1, labelKey: "personalSheet.sections.scientificWorks" },
  { id: "government_awards", icon: Medal, labelKey: "personalSheet.sections.governmentAwards" },
  { id: "social_categories", icon: Heart, labelKey: "personalSheet.sections.socialCategories" },
];

const SECTION_ORDER: SectionId[] = ["general", "contacts", "addresses", "emergency", "id_documents", "banking", "education", "experience", "languages", "scientific_works", "government_awards", "social_categories"];
const SECTION_API_NAMES = ["general", "contacts", "addresses", "emergency_contacts", "id_documents", "banking", "education", "experience", "languages", "scientific_works", "government_awards", "social_categories"];

interface Props {
  personalSheet: PersonalSheet;
  workerId: number;
  isEditMode: boolean;
  onEditModeChange: (value: boolean) => void;
  isChiefDirectEdit?: boolean;
}

export default function PersonalSheetTab({
  personalSheet,
  workerId,
  isEditMode,
  onEditModeChange,
  isChiefDirectEdit = false,
}: Props) {
  const { t } = useTranslation("EmployeePortal");
  const [activeSection, setActiveSection] = useState<SectionId>("general");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<{ changes: Record<string, unknown>; files: Record<string, File> } | null>(null);

  const [createDataChangeForWorker] = useCreateDataChangeForWorkerMutation();
  const {
    data: editDraftResponse,
    isLoading: isEditDraftLoading,
    isError: isEditDraftError,
  } = useGetWorkerDataChangeDraftQuery(workerId, { skip: !isEditMode });
  const [upsertWorkerDataChangeDraft] = useUpsertWorkerDataChangeDraftMutation();
  const [deleteWorkerDataChangeDraft] = useDeleteWorkerDataChangeDraftMutation();
  const [uploadWorkerDataChangeDraftFile] = useUploadWorkerDataChangeDraftFileMutation();
  const [editDraftToast, setEditDraftToast] = useState<"saved" | "error" | null>(null);
  const editDraftRestoredRef = useRef(false);

  const editDraftSheet = useMemo(
    function editDraftSheet() {
      if (!editDraftResponse?.has_draft) return null;
      return enrichDraftFilesFromStaged(editDraftResponse.data, editDraftResponse.uploaded_files);
    },
    [editDraftResponse],
  );

  const generalRef = useRef<SectionFormHandle>(null);
  const contactsRef = useRef<SectionFormHandle>(null);
  const addressesRef = useRef<SectionFormHandle>(null);
  const emergencyRef = useRef<SectionFormHandle>(null);
  const idDocumentsRef = useRef<SectionFormHandle>(null);
  const bankingRef = useRef<SectionFormHandle>(null);
  const educationRef = useRef<SectionFormHandle>(null);
  const experienceRef = useRef<SectionFormHandle>(null);
  const languagesRef = useRef<SectionFormHandle>(null);
  const scientificWorksRef = useRef<SectionFormHandle>(null);
  const governmentAwardsRef = useRef<SectionFormHandle>(null);
  const socialCategoriesRef = useRef<SectionFormHandle>(null);

  const allRefs = [generalRef, contactsRef, addressesRef, emergencyRef, idDocumentsRef, bankingRef, educationRef, experienceRef, languagesRef, scientificWorksRef, governmentAwardsRef, socialCategoriesRef];

  const handleExitEditMode = useCallback(function handleExitEditMode() {
    onEditModeChange(false);
    setPendingChanges(null);
    setShowConfirmModal(false);
    editDraftRestoredRef.current = false;
  }, [onEditModeChange]);

  function isAnyFormDirty(): boolean {
    return allRefs.some((ref) => ref.current?.isDirty());
  }

  function toDraftSectionKey(section: SectionId): DraftSectionKey {
    return (section === "emergency" ? "emergency_contacts" : section) as DraftSectionKey;
  }

  function fromDraftSectionKey(section: DraftSectionKey): SectionId {
    return (section === "emergency_contacts" ? "emergency" : section) as SectionId;
  }

  function buildWorkerSheet(): PersonalSheet {
    return buildMergedSheet(personalSheet, {
      generalRef,
      contactsRef,
      addressesRef,
      emergencyRef,
      idDocumentsRef,
      bankingRef,
      educationRef,
      experienceRef,
      languagesRef,
      scientificWorksRef,
      governmentAwardsRef,
      socialCategoriesRef,
    });
  }

  function handleSectionChange(section: SectionId) {
    setActiveSection(section);
    if (isEditMode && isAnyFormDirty()) {
      void persistEditDraft(section);
    }
  }

  async function persistEditDraft(section: SectionId) {
    if (isEditDraftError) return;
    const draftFiles = allRefs.reduce<Record<string, File>>((acc, ref) => {
      const files = ref.current?.getFiles();
      return files ? { ...acc, ...files } : acc;
    }, {});
    try {
      await Promise.all(
        Object.entries(draftFiles).map(([fieldName, file]) => {
          const formData = new FormData();
          formData.append("field_name", fieldName);
          formData.append("file", file);
          return uploadWorkerDataChangeDraftFile({ workerId, body: formData }).unwrap();
        }),
      );
      await upsertWorkerDataChangeDraft({
        workerId,
        data: buildWorkerSheet(),
        last_active_section: toDraftSectionKey(section),
      }).unwrap();
      setEditDraftToast("saved");
    } catch {
      setEditDraftToast("error");
    }
  }

  useEffect(
    function restoreEditDraftSection() {
      if (!isEditMode) return;
      if (editDraftRestoredRef.current || isEditDraftLoading) return;
      editDraftRestoredRef.current = true;
      if (editDraftResponse?.has_draft && editDraftResponse.last_active_section) {
        setActiveSection(fromDraftSectionKey(editDraftResponse.last_active_section));
      }
    },
    [editDraftResponse, isEditDraftLoading, isEditMode],
  );

  async function handleSubmitChanges() {
    const validations = await Promise.all(
      allRefs.map((ref) => {
        if (!ref.current?.isDirty()) return Promise.resolve(true);
        return ref.current.validate();
      }),
    );
    const firstErrorIdx = validations.findIndex((v) => !v);
    if (firstErrorIdx !== -1) {
      setActiveSection(SECTION_ORDER[firstErrorIdx]);
      return;
    }

    const allChanges: Record<string, unknown> = {};
    const allFiles: Record<string, File> = {};

    allRefs.forEach((ref, idx) => {
      const changes = ref.current?.getChanges();
      const files = ref.current?.getFiles();
      if (changes && Object.keys(changes).length > 0) {
        allChanges[SECTION_API_NAMES[idx]] = changes;
      }
      if (files) {
        Object.assign(allFiles, files);
      }
    });

    for (const ref of allRefs) {
      const flags = ref.current?.getFlagChanges?.();
      if (flags) {
        if (!allChanges["general"]) allChanges["general"] = {};
        Object.assign(allChanges["general"] as Record<string, unknown>, flags);
      }
    }

    if (allFiles["kyc_documents.worker_photo_file"] && !allChanges["kyc_documents"]) {
      allChanges["kyc_documents"] = {};
    }

    if (Object.keys(allChanges).length === 0 && Object.keys(allFiles).length === 0) {
      setToast(t("personalSheet.edit.noChanges"));
      return;
    }

    if (isFioChangeMissingIdFile(allChanges, allFiles)) {
      setActiveSection("id_documents");
      setToast(t("personalSheet.edit.fioRequiresIdFile"));
      return;
    }

    setPendingChanges({ changes: allChanges, files: allFiles });
    setShowConfirmModal(true);
  }

  async function handleConfirmSubmit() {
    if (!pendingChanges) return;

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("worker_id", String(workerId));
      formData.append("sections", JSON.stringify(pendingChanges.changes));

      for (const [key, file] of Object.entries(pendingChanges.files)) {
        formData.append(key, file);
      }

      await createDataChangeForWorker(formData).unwrap();
      void deleteWorkerDataChangeDraft(workerId);
      editDraftRestoredRef.current = false;
      setShowConfirmModal(false);
      setPendingChanges(null);
      onEditModeChange(false);
      setPrompt({
        title: isChiefDirectEdit ? t("personalSheet.edit.chiefSuccessTitle") : t("personalSheet.edit.successTitle"),
        text: isChiefDirectEdit ? t("personalSheet.edit.chiefSuccessText") : t("personalSheet.edit.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setShowConfirmModal(false);
      setPrompt({
        title: t("personalSheet.edit.errorTitle"),
        text: extractErrorMessage(err, "personalSheet.edit.errorText", t),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const steps = SECTIONS.map(function toStep(section) {
    return { id: section.id, label: t(section.labelKey), icon: section.icon };
  });

  function renderViewSection() {
    switch (activeSection) {
      case "general":
        return (
          <GeneralSection
            general={personalSheet.general}
            enrollmentVerificationUrl={personalSheet.kyc_documents?.enrollment_verification_file_url ?? null}
            workerAgreementUrl={personalSheet.kyc_documents?.worker_agreement_file_url ?? null}
          />
        );
      case "contacts":
        return <ContactsSection contacts={personalSheet.contacts} />;
      case "addresses":
        return <AddressesSection addresses={personalSheet.addresses} />;
      case "emergency":
        return <EmergencyContactsSection contacts={personalSheet.emergency_contacts} />;
      case "id_documents":
        return <IdDocumentsSection idDocuments={personalSheet.id_documents} />;
      case "banking":
        return <BankingSection banking={personalSheet.banking} />;
      case "education":
        return <EducationSection records={personalSheet.education} hasNoEducation={personalSheet.general.has_no_education} />;
      case "experience":
        return <ExperienceSection records={personalSheet.experience} hasNoExperience={personalSheet.general.has_no_work_experience} workProofUrl={personalSheet.kyc_documents?.work_proof_file_url ?? null} />;
      case "languages":
        return <LanguagesSection records={personalSheet.languages} />;
      case "scientific_works":
        return <ScientificWorksSection records={personalSheet.scientific_works} hasNoScientificWorks={personalSheet.general.has_no_scientific_works} />;
      case "government_awards":
        return <GovernmentAwardsSection records={personalSheet.government_awards} hasNoGovernmentAwards={personalSheet.general.has_no_government_awards} />;
      case "social_categories":
        return <SocialCategoriesSection records={personalSheet.social_categories} hasNoSocialCategories={personalSheet.general.has_no_social_categories} />;
      default:
        return null;
    }
  }

  function renderEditContent() {
    if (isEditDraftLoading) {
      return (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      );
    }
    return (
      <div>
        <div hidden={activeSection !== "general"}>
          <EditGeneralSection ref={generalRef} general={personalSheet.general} draftValues={editDraftSheet?.general} />
        </div>
        <div hidden={activeSection !== "contacts"}>
          <EditContactsSection ref={contactsRef} contacts={personalSheet.contacts} draftValues={editDraftSheet?.contacts} />
        </div>
        <div hidden={activeSection !== "addresses"}>
          <EditAddressesSection ref={addressesRef} addresses={personalSheet.addresses} draftValues={editDraftSheet?.addresses} />
        </div>
        <div hidden={activeSection !== "emergency"}>
          <EditEmergencyContactsSection ref={emergencyRef} contacts={personalSheet.emergency_contacts} draftAddedRecords={editDraftSheet ? editDraftSheet.emergency_contacts.slice(personalSheet.emergency_contacts.length) : undefined} />
        </div>
        <div hidden={activeSection !== "id_documents"}>
          <EditIdDocumentsSection
            ref={idDocumentsRef}
            idDocuments={personalSheet.id_documents}
            gender={personalSheet.general.gender}
            isResident={personalSheet.general.is_resident}
            draftValues={editDraftSheet?.id_documents}
          />
        </div>
        <div hidden={activeSection !== "banking"}>
          <EditBankingSection ref={bankingRef} banking={personalSheet.banking} draftValues={editDraftSheet?.banking} />
        </div>
        <div hidden={activeSection !== "education"}>
          <EditEducationSection ref={educationRef} records={personalSheet.education} initialHasNoEducation={personalSheet.general.has_no_education ?? false} draftAddedRecords={editDraftSheet ? editDraftSheet.education.slice(personalSheet.education.length) : undefined} />
        </div>
        <div hidden={activeSection !== "experience"}>
          <EditExperienceSection ref={experienceRef} records={personalSheet.experience} initialHasNoExperience={personalSheet.general.has_no_work_experience ?? false} draftAddedRecords={editDraftSheet ? editDraftSheet.experience.slice(personalSheet.experience.length) : undefined} />
        </div>
        <div hidden={activeSection !== "languages"}>
          <EditLanguagesSection ref={languagesRef} records={personalSheet.languages} draftAddedRecords={editDraftSheet ? editDraftSheet.languages.slice(personalSheet.languages.length) : undefined} />
        </div>
        <div hidden={activeSection !== "scientific_works"}>
          <EditScientificWorksSection ref={scientificWorksRef} records={personalSheet.scientific_works} initialHasNoScientificWorks={personalSheet.general.has_no_scientific_works ?? false} draftAddedRecords={editDraftSheet ? editDraftSheet.scientific_works.slice(personalSheet.scientific_works.length) : undefined} />
        </div>
        <div hidden={activeSection !== "government_awards"}>
          <EditGovernmentAwardsSection ref={governmentAwardsRef} records={personalSheet.government_awards} initialHasNoGovernmentAwards={personalSheet.general.has_no_government_awards ?? false} draftAddedRecords={editDraftSheet ? editDraftSheet.government_awards.slice(personalSheet.government_awards.length) : undefined} />
        </div>
        <div hidden={activeSection !== "social_categories"}>
          <EditSocialCategoriesSection ref={socialCategoriesRef} records={personalSheet.social_categories} initialHasNoSocialCategories={personalSheet.general.has_no_social_categories ?? false} draftAddedRecords={editDraftSheet ? editDraftSheet.social_categories.slice(personalSheet.social_categories.length) : undefined} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="radius-3xl surface-base-fill p-7">
        {toast && (
          <div className="mb-5">
            <Toast color="notice" text={toast} isFullWidth autoClose={false} onClose={() => setToast(null)} />
          </div>
        )}
        {editDraftToast && (
          <div className="fixed bottom-6 right-6 z-50">
            <Toast
              key={editDraftToast}
              color={editDraftToast === "saved" ? "positive" : "negative"}
              text={editDraftToast === "saved" ? t("personalSheet.edit.draftSaved") : t("personalSheet.edit.draftSaveFailed")}
              autoCloseDuration={5000}
              onClose={() => setEditDraftToast(null)}
            />
          </div>
        )}
        <StepsSwitcher
          steps={steps}
          activeStep={activeSection}
          onStepChange={(stepId) => handleSectionChange(stepId as SectionId)}
          className="p-0!"
          contentClassName="surface-component-stroke!"
          hideNumbers={true}>
          {isEditMode ? renderEditContent() : renderViewSection()}
        </StepsSwitcher>
        {isEditMode && (
          <div className="sticky bottom-0 mt-6 p-4 surface-base-fill flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={handleExitEditMode}>
              {t("personalSheet.edit.cancel")}
            </Button>
            <Button variant="primary" onClick={handleSubmitChanges}>
              {isChiefDirectEdit ? t("personalSheet.edit.chiefSubmit") : t("personalSheet.edit.submit")}
            </Button>
          </div>
        )}
      </div>

      {showConfirmModal && pendingChanges && (
        <ConfirmChangesModal
          changes={pendingChanges.changes as Record<string, Record<string, unknown> | unknown[]>}
          files={pendingChanges.files}
          personalSheet={personalSheet}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSubmit}
          isSubmitting={isSubmitting}
          isChiefDirectEdit={isChiefDirectEdit}
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="EmployeePortal"
        />
      )}
    </>
  );
}
